import React from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { FormGroup, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppInput from "components/Input/AppInput";
import { DAYS_OF_WEEK, SHIFT } from "config/AppConfig";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import moment from "moment-timezone";
import SelectField from 'components/Select/SelectField';
import SelectTime from 'components/Select/SelectTime';
import { Label } from 'reactstrap';
import SegmentFieldArray from 'views/Company/Jobs/component/SegmentFieldArray';

const useStyles = makeStyles(() => ({
  textarea: {
    height: '100px !important',
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '1rem'
  },
  radio: {
    color: AppColor.black,
    "& .MuiFormControlLabel-label": {
      fontSize: '1rem',
      fontWeight: 700
    },
  },
  radioGroup: {
    flexDirection: 'row'
  },
  postDescription: {
    border: '1px solid rgba(0, 0, 0, 0.2)',
    padding: '0.5rem 1rem',
    color: AppColor.black
  },
  bold: {
    fontWeight: 700
  },
  wrapShift: {
    border: '1px solid #c4b5bc',
    padding: '1rem',
    boxShadow: '0px 4px 4px rgb(0 0 0 / 25%)',
    borderRadius: 4,
    marginBottom: '0.5rem',
    '& .shiftInfo': {
      margin: '-1rem -1rem 1rem -1rem',
      padding: '0.3rem 1rem',
      border: '1px solid #c4b5bc',
      borderRadius: 4,
      backgroundColor: AppColor.white
    }
  },
  collapseShift: {
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 1rem',
    margin: '0.5rem 0',
    '& .shiftInfo': {
      margin: 0,
      padding: '0.3rem 1rem',
      border: '1px solid #c4b5bc',
      borderRadius: 4,
      flex: '1 0 100%',
      backgroundColor: AppColor.white
    },
    '& .remove-icon': {
      fontSize: '2rem',
      color: AppColor.red
    }
  },
  title: {
    display: 'flex',
    margin: '2rem 0 1rem 0',
    '& h5': {
      color: AppColor.black,
    }
  },
  calendar: {
    cursor: 'pointer'
  }
}));

const ShiftForm = props => {
  const {
    setIsLoading,
    jobDetail,
    isSubmitting,
    onSubmit,
    onCancel,
    newShift,
    jobAvailabilities,
    checkValidDate
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const {  ignoreDay = [], dayOfWeek: selectedDate } = newShift;

  const dayOptions = DAYS_OF_WEEK.filter(day => !ignoreDay.includes(day) && checkValidDate(day)).map(item => {
    return {
      value: item,
      label: t(`daysOfTheWeek.${item}`)
    }
  });
  const shiftOptions = SHIFT.map(item => {
    return {
      value: item,
      label: t(`shift.${item}`)
    }
  });

  const defaultWorkHistory = {
    dayOfWeek: newShift.dayOfWeek
      ? newShift.dayOfWeek
      : dayOptions.length > 0
        ? dayOptions[0].value
        : '',
    shiftType: newShift.shiftType
      ? newShift.shiftType
      : shiftOptions.length > 0
        ? shiftOptions[0].value
        : '',
    startTime: '',
    endTime: '',
    numberWorkers: '',
    isExpand: true,
    segments: []
  };

  const isApplyTime = (monday, sunday, timeApply) => {
    const applyStartDate = moment.utc(timeApply.applyStartDate);
    const applyEndDate = moment.utc(timeApply.applyEndDate);
    if (moment.range(monday, applyEndDate).diff('second') > 0 
    && moment.range(applyStartDate, sunday).diff('second') > 0) {
        return true;
    }
    return false;
  }

  const _handleSelectChangeEndTime = (startTime, event, setFieldValue) => {
    const date = jobDetail.startDate ? moment(jobDetail.startDate) : moment();
    event = moment(new Date(event)).set({
      year: date.get('year'),
      month: date.get('month'),
      date: date.get('date')
    });

    const momentStartTime = moment(startTime);
    if (moment.range(momentStartTime, event).diff('minutes') <= 0) {
      event.add(1, 'days');
    }
    setFieldValue('endTime', event.format('YYYY/MM/DD HH:mm'))
  }
  const _handleSelectChangeStartTime = (endTime, event, setFieldValue) => {
    const date = jobDetail.startDate ? moment(jobDetail.startDate) : moment();
    event = moment(new Date(event)).set({
        year: date.get('year'),
        month: date.get('month'),
        date: date.get('date')
    });
    const momentEndTime = moment(endTime);
    if (moment.range(event, momentEndTime).diff('minutes') <= 0) {
      momentEndTime.add(1, 'days');
      setFieldValue('endTime', momentEndTime.format('YYYY/MM/DD HH:mm'));
    }
    setFieldValue('startTime', event.format('YYYY/MM/DD HH:mm'));
  }

  const segmentValidate = Yup.object()
    .shape(
      {
        endTime: Yup.date()
        .required("validation.required")
        .when("startTime", (startTime, schema) => {
          if (startTime && startTime !== "") {
            return schema.min(
              moment(startTime)
                .add(1, "minutes")
                .format("yyyy/MM/DD HH:mm"),
              "validation.endTimeMustBeAfterStartTime"
            );
          }
        }),
        startTime: Yup.date()
        .required("validation.required")
        .when("endTime", (endTime, schema) => {
          if (endTime && endTime !== '') {
            return schema.max(
              moment(endTime)
                .subtract(1, "minutes")
                .format("yyyy/MM/DD HH:mm"),
              "validation.startTimeMustBeBeforeEndTime"
            );
          }
        }),
        shiftType: Yup.string().required("validation.required"),
      },
      // determine relationship
      [
        ["startTime", "endTime"],
        ["endTime", "startTime"],
      ]
    )
    // .test("duplicates-shift-type", "validation.duplicatesShiftType", function (segment) {  
    //   const { path, createError } = this;  
    //   const segments = this.parent;
    //   if (!segments) return true;
    //   const shiftTypes = segments.map(seg => seg.shiftType);
    
    //   // Ki?m tra xem shiftType hi?n t?i co ph?i la duy nh?t khong  
    //   if (shiftTypes.indexOf(segment.shiftType) !== shiftTypes.lastIndexOf(segment.shiftType)) {
    //     return createError({ path: `${path}.shiftType`, message: "shiftType must be unique" });
    //   }
    //   return true;
    // });
  const shiftValidate = Yup.object().shape({
    startTime: Yup.date()
      .required("validation.required")
      .when('endTime', (endTime, schema) => {
        if (endTime && endTime !== '') {
          return schema.max(
            moment(endTime).subtract(1, 'minutes').format('yyyy/MM/DD HH:mm'),
            "validation.startTimeMustBeBeforeEndTime"
          )
        }
      }),
    endTime: Yup.date()
      .required("validation.required")
      .when('startTime', (startTime, schema) => {
        if (startTime && startTime !== '') {
          return schema.min(
            moment(startTime).add(1, 'minutes').format('yyyy/MM/DD HH:mm'),
            "validation.endTimeMustBeAfterStartTime"
          )
        }
      }),
    numberWorkers: Yup.number().required("validation.required")
    .min(1, { message: "validation.minValue", value: 1 })
    .max(100, { message: "validation.maxValue", value: 100 }),
    segments: Yup.array().of(segmentValidate),

    // .test('len', "validation.max_length_10", val => Math.ceil(Math.log10(val + 1)) <= 10)
  },
    [
      ["startTime", "endTime"],
      ["endTime", "startTime"],
    ]
  );

  const shiftConstructor = (item, startDate) =>{
    const newStartTime = item.startTime || item.startTime !== '' ? moment(item.startTime).set({
      year: startDate.get('year'),
      month: startDate.get('month'),
      date: startDate.get('date')
    }) : null;
    const newEndTime = item.endTime || item.endTime !== '' ? moment(item.endTime).set({
      year: startDate.get('year'),
      month: startDate.get('month'),
      date: startDate.get('date')
    }) : null;

    if (newStartTime && newEndTime) {
      if (moment.range(newStartTime, newEndTime).diff('minutes') <= 0) {
        newEndTime.add(1, 'days');
      }
    }
    return {
      ...item,
      startTime: newStartTime ? newStartTime.format('YYYY/MM/DD HH:mm') : null,
      endTime: newEndTime ? newEndTime.format('YYYY/MM/DD HH:mm') : null
    }
  };
  const handleSubmit = (data) => {
    setIsLoading(true);
    const { dayOfWeek, ...params } = data;
    params.groupByDayOfWeek = {
      [dayOfWeek] : { status: 'ACTIVE' }
    }
    onSubmit(params);
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={defaultWorkHistory}
        validationSchema={shiftValidate}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, setFieldValue }) => {
          const {
            dayOfWeek,
            shiftType,
            startTime,
            endTime,
          } = values;
          const startDate = jobDetail.startDate ? moment(jobDetail.startDate) : moment();
          const endDate = jobDetail.endDate ? moment(jobDetail.endDate) : moment();
          let startApply = moment(startDate);
          if (moment.range(startApply, moment(moment().format('yyyy/MM/DD'))).diff('minutes')) {
            startApply = moment(moment().format('yyyy/MM/DD'));
          }
          const availabilitySameDay = jobAvailabilities
          .filter(item => {
            if (item.dayOfWeek !== dayOfWeek || item.shiftType !== shiftType || item.status === 'INACTIVE') {
              return false;
            }
            // case aplly for current week
            return isApplyTime(moment.utc(startApply.format('yyyy/MM/DD')), moment.utc(endDate.format('yyyy/MM/DD')), item);
          })
          .map((item) => {
            return shiftConstructor(item, startDate)
          })
          let isConflict = false;
          const currentShift = shiftConstructor({startTime, endTime, shiftType}, startDate);

          // CHECK isConflict for currentShift
          if (currentShift && currentShift.startTime && currentShift.endTime) {
            for (const i in availabilitySameDay) {
              if (availabilitySameDay[i].startTime && availabilitySameDay[i].endTime
                && availabilitySameDay[i].startTime === currentShift.startTime
                && availabilitySameDay[i].endTime === currentShift.endTime) {
                isConflict = true;
                break;
              }
            }
          }
          return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Field
                    component={SelectField}
                    name={`dayOfWeek`}
                    label={"label.daysOfTheWeek"}
                    options={dayOptions}
                    disabled={!!selectedDate}
                    onSelected={() => {
                      setFieldValue(
                        `shiftType`,
                        ""
                      );

                      setFieldValue(
                        `startTime`,
                        moment(new Date())
                          .hours(0)
                          .minutes(0)
                          .format("YYYY/MM/DD HH:mm")
                      );
                      setFieldValue(
                        `endTime`,
                        moment(new Date())
                          .hours(0)
                          .minutes(0)
                          .format("YYYY/MM/DD HH:mm")
                      );
                    }}
                    required
                  />
                  <Field
                    component={SelectField}
                    name={`shiftType`}
                    label={"label.shift"}
                    options={shiftOptions}
                    onSelected={() => {
                      setFieldValue(
                        `startTime`,
                        ""
                      );
                      setFieldValue(
                        `endTime`,
                        ""
                      );
                    }}
                    required
                  />
                  {isConflict && (
                    <Label
                      className={`${classes.label}`}
                      style={{ color: AppColor.red }}
                    >
                      {t("validation.conflictTime")}
                    </Label>
                  )}
                  {/* Start Time End Time */}
                  <Field
                    component={SelectTime}
                    name={`startTime`}
                    onSelected={(event) => _handleSelectChangeStartTime(endTime, event, setFieldValue)}
                    label={"label.startTime"}
                    disabled={ !shiftType || shiftType === "" }
                    required
                  />
                  <Field
                    component={SelectTime}
                    name={`endTime`}
                    label={"label.endTime"}
                    onSelected={(event) => _handleSelectChangeEndTime(startTime, event, setFieldValue)}
                    disabled={ !shiftType || shiftType === "" }
                    required
                  />
                  <Field
                    component={AppInput}
                    name={`numberWorkers`}
                    label={"label.numberOfStaffs"}
                    type="number"
                    required
                  />
                  {
                    startTime && endTime && shiftType &&
                    <SegmentFieldArray
                      startDate={jobDetail.startDate}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      jobAvailability={values}
                      fieldArrayName={`segments`}
                      minTime={moment(startTime)}
                      maxTime={moment(endTime)}
                    />
                  }
                </Grid>
              </Grid>
              <FormGroup className={classes.control}>
                <AppButton
                  label={`${t("button.close")}`}
                  rounder
                  mode="outlined"
                  onClick={onCancel}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
                <AppButton
                  className='ml-1'
                  label={`${t("button.save")}`}
                  rounder
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </FormGroup>
            </Form>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}

ShiftForm.propTypes = {
  setIsLoading: PropTypes.func,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  newShift: PropTypes.any,
  isSubmitting: PropTypes.bool,
  jobAvailabilities: PropTypes.any,
  checkValidDate: PropTypes.func,
  jobDetail: PropTypes.any
}

export default ShiftForm;
